import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, Chip, Avatar, Alert, Skeleton,
  IconButton, Tooltip, useMediaQuery, useTheme, Menu, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import {
  Save as SaveIcon, DragIndicator as DragIcon,
  Science as ScienceIcon, Bluetooth as BleIcon,
  Refresh as RefreshIcon,
  SettingsInputComponent as StationIcon,
  Add as AddIcon,
  PlaylistAdd as AddAllIcon,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { locationAPI, stationAPI, testTypeAPI } from '../services/api';
import toast from 'react-hot-toast';

const categoryColors = {
  ble_vital: { bg: '#ecfeff', text: '#0891b2', label: 'BLE Vital' },
  ble_lab: { bg: '#eff6ff', text: '#2563eb', label: 'BLE Lab' },
  manual_lab: { bg: '#fffbeb', text: '#d97706', label: 'Manual Lab' },
  manual_history: { bg: '#ecfdf5', text: '#059669', label: 'History' },
};

// Draggable test item
function SortableTestItem({ test, allTests, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: test,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const testInfo = allTests.find(t => t.testId === test);
  const cat = categoryColors[testInfo?.category] || categoryColors.manual_lab;

  return (
    <Box ref={setNodeRef} style={style} sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: { xs: 1, sm: 1.5 }, mb: 1,
      borderRadius: 2, bgcolor: '#fff', border: '1px solid #e0f2fe',
      cursor: 'grab', transition: 'all 0.15s',
      '&:hover': { borderColor: '#67e8f9', boxShadow: '0 2px 8px rgba(8,145,178,0.1)' },
    }}>
      <Box {...attributes} {...listeners} sx={{ color: '#94a3b8', display: 'flex', touchAction: 'none' }}>
        <DragIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }} noWrap>
          {testInfo?.name || test}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={cat.label} size="small"
            sx={{ height: 20, fontSize: '0.65rem', bgcolor: cat.bg, color: cat.text, fontWeight: 600 }}
          />
          {testInfo?.bleDevice && (
            <Chip
              icon={<BleIcon sx={{ fontSize: '0.7rem !important' }} />}
              label={testInfo.bleDevice} size="small"
              sx={{ height: 20, fontSize: '0.65rem' }}
              variant="outlined"
            />
          )}
          {testInfo?.estimatedMinutes && (
            <Typography variant="caption" color="text.secondary">
              ~{testInfo.estimatedMinutes}min
            </Typography>
          )}
        </Box>
      </Box>
      <Tooltip title="Remove from station">
        <IconButton size="small" onClick={() => onRemove(test)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
          &times;
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// Overlay item shown while dragging
function DragOverlayItem({ testId, allTests }) {
  const testInfo = allTests.find(t => t.testId === testId);
  const cat = categoryColors[testInfo?.category] || categoryColors.manual_lab;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
      borderRadius: 2, bgcolor: '#fff', border: '2px solid #0891b2',
      boxShadow: '0 8px 25px rgba(8,145,178,0.25)', cursor: 'grabbing',
    }}>
      <DragIcon fontSize="small" sx={{ color: '#0891b2' }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>{testInfo?.name || testId}</Typography>
        <Chip label={cat.label} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: cat.bg, color: cat.text, mt: 0.5 }} />
      </Box>
    </Box>
  );
}

function StationConfig() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [stations, setStations] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeStationIdx, setActiveStationIdx] = useState(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    Promise.all([
      locationAPI.getAll(),
      testTypeAPI.getAll(),
    ]).then(([locRes, testRes]) => {
      setLocations(locRes.data.data || []);
      setAllTests(testRes.data.data || []);
    }).catch(() => toast.error('Failed to load data'));
  }, []);

  useEffect(() => {
    if (selectedLocation) fetchStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const fetchStations = async () => {
    try {
      setLoading(true);
      const res = await stationAPI.getByLocation(selectedLocation);
      setStations(res.data.data || []);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load stations');
    } finally {
      setLoading(false);
    }
  };

  // Per-station "Add Test" menu state
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addMenuStationIdx, setAddMenuStationIdx] = useState(null);

  // Get available tests for a station (tests not yet in THAT station — can be in other stations)
  const getAvailableTests = (stationIdx) => {
    const stationTests = stations[stationIdx]?.assignedTests || [];
    return allTests.filter(t => !stationTests.includes(t.testId));
  };

  // Total unique assignments across all stations
  const totalAssignments = stations.reduce((sum, s) => sum + (s.assignedTests || []).length, 0);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveStationIdx(null);
    if (!over || active.id === over.id) return;

    // Find which station has the active and over items
    const stationIdx = stations.findIndex(s => (s.assignedTests || []).includes(active.id));
    if (stationIdx === -1) return;

    const tests = [...stations[stationIdx].assignedTests];
    const oldIndex = tests.indexOf(active.id);
    const newIndex = tests.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newStations = [...stations];
    newStations[stationIdx] = {
      ...newStations[stationIdx],
      assignedTests: arrayMove(tests, oldIndex, newIndex),
    };
    setStations(newStations);
    setHasChanges(true);
  }, [stations]);

  const addTestToStation = (stationIdx, testId) => {
    const newStations = [...stations];
    const current = [...(newStations[stationIdx].assignedTests || [])];
    if (!current.includes(testId)) {
      current.push(testId);
      newStations[stationIdx] = { ...newStations[stationIdx], assignedTests: current };
      setStations(newStations);
      setHasChanges(true);
    }
  };

  const removeTestFromStation = (stationIdx, testId) => {
    const newStations = [...stations];
    newStations[stationIdx] = {
      ...newStations[stationIdx],
      assignedTests: (newStations[stationIdx].assignedTests || []).filter(t => t !== testId),
    };
    setStations(newStations);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await stationAPI.bulkUpdateTests(
        stations.map(s => ({ stationId: s._id, assignedTests: s.assignedTests || [] }))
      );
      toast.success('Station configuration saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Auto-distribute tests evenly across stations
  const autoDistribute = () => {
    const allTestIds = allTests.map(t => t.testId);
    const numStations = stations.length;
    if (numStations === 0) return;

    const newStations = stations.map(s => ({ ...s, assignedTests: [] }));
    allTestIds.forEach((testId, i) => {
      newStations[i % numStations].assignedTests.push(testId);
    });
    setStations(newStations);
    setHasChanges(true);
    toast.success('Tests distributed evenly');
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 3, flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Typography variant={isSmall ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#0f172a' }}>
            Station Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Drag and drop tests to assign them to stations
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel>Select Location</InputLabel>
          <Select
            value={selectedLocation} label="Select Location"
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!selectedLocation ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <StationIcon sx={{ fontSize: 64, color: '#67e8f9', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Select a location</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a location to configure its stations and test assignments
          </Typography>
        </Card>
      ) : loading ? (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {[1, 2, 3].map(i => <Card key={i} sx={{ flex: 1, p: 3 }}><Skeleton height={300} /></Card>)}
        </Box>
      ) : (
        <>
          {/* Action bar */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            mb: 3, p: 2, borderRadius: 3, bgcolor: '#f0f9ff', border: '1px solid #e0f2fe',
            flexWrap: 'wrap', gap: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={autoDistribute}>
                Auto-Distribute
              </Button>
              <Typography variant="body2" color="text.secondary">
                {totalAssignments} test assignments across {stations.length} stations
              </Typography>
            </Box>
            <Button
              variant="contained" startIcon={<SaveIcon />}
              disabled={!hasChanges || saving}
              onClick={handleSave}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>

          {hasChanges && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
              You have unsaved changes. Click "Save Configuration" to apply.
            </Alert>
          )}

          {/* Per-station Add Test menu */}
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={() => { setAddMenuAnchor(null); setAddMenuStationIdx(null); }}
            PaperProps={{ sx: { maxHeight: 400, minWidth: 280 } }}
          >
            <Typography variant="caption" sx={{ px: 2, py: 0.5, color: '#64748b', fontWeight: 600, display: 'block' }}>
              Add test to {addMenuStationIdx !== null && stations[addMenuStationIdx]?.name}:
            </Typography>
            {addMenuStationIdx !== null && (() => {
              const available = getAvailableTests(addMenuStationIdx);
              if (available.length === 0) return (
                <MenuItem disabled><ListItemText>All tests already added</ListItemText></MenuItem>
              );
              return [
                <MenuItem key="__all" onClick={() => {
                  const newStations = [...stations];
                  const allTestIds = allTests.map(t => t.testId);
                  newStations[addMenuStationIdx] = { ...newStations[addMenuStationIdx], assignedTests: allTestIds };
                  setStations(newStations);
                  setHasChanges(true);
                  setAddMenuAnchor(null);
                  setAddMenuStationIdx(null);
                  toast.success(`All ${allTestIds.length} tests added`);
                }}>
                  <ListItemIcon><AddAllIcon sx={{ color: '#0891b2' }} /></ListItemIcon>
                  <ListItemText sx={{ fontWeight: 600 }}>Add All ({available.length} remaining)</ListItemText>
                </MenuItem>,
                ...available.map(test => {
                  const cat = categoryColors[test.category] || categoryColors.manual_lab;
                  return (
                    <MenuItem key={test.testId} onClick={() => {
                      addTestToStation(addMenuStationIdx, test.testId);
                      // Keep menu open for adding more tests
                    }}>
                      <ListItemIcon>
                        {test.bleDevice ? <BleIcon sx={{ color: cat.text, fontSize: '1.1rem' }} /> : <ScienceIcon sx={{ color: cat.text, fontSize: '1.1rem' }} />}
                      </ListItemIcon>
                      <ListItemText>{test.name}</ListItemText>
                      <Chip label={cat.label} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: cat.bg, color: cat.text, ml: 1 }} />
                    </MenuItem>
                  );
                })
              ];
            })()}
          </Menu>

          {/* Station columns */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: stations.length > 1 ? 'repeat(2, 1fr)' : '1fr',
              md: `repeat(${Math.min(stations.length, 3)}, 1fr)`,
            },
            gap: { xs: 2, md: 3 },
          }}>
            {stations.map((station, stationIdx) => {
              const tests = station.assignedTests || [];
              const totalMinutes = tests.reduce((sum, tid) => {
                const t = allTests.find(x => x.testId === tid);
                return sum + (t?.estimatedMinutes || 0);
              }, 0);

              return (
                <Card key={station._id} sx={{
                  border: activeStationIdx === stationIdx ? '2px solid #0891b2' : '1px solid #e0f2fe',
                  transition: 'border-color 0.2s',
                }}>
                  {/* Station header */}
                  <Box sx={{
                    p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfeff 100%)',
                    borderBottom: '1px solid #e0f2fe',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{
                        width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700,
                        background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                      }}>
                        {station.stationNumber}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {station.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tests.length} tests &middot; ~{totalMinutes} min
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title="Add tests to this station">
                      <IconButton size="small" onClick={(e) => {
                        setAddMenuStationIdx(stationIdx);
                        setAddMenuAnchor(e.currentTarget);
                      }} sx={{ color: '#0891b2' }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Drag-and-drop area */}
                  <CardContent sx={{ p: 1.5, minHeight: 200, bgcolor: '#fafafa' }}>
                    {tests.length === 0 ? (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        height: 150, border: '2px dashed #e0f2fe', borderRadius: 3,
                        color: '#94a3b8', flexDirection: 'column',
                      }}>
                        <Typography variant="body2">No tests assigned</Typography>
                        <Typography variant="caption">Click + to add tests</Typography>
                      </Box>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={(e) => { handleDragStart(e); setActiveStationIdx(stationIdx); }}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={tests} strategy={verticalListSortingStrategy}>
                          {tests.map((testId) => (
                            <SortableTestItem
                              key={testId}
                              test={testId}
                              allTests={allTests}
                              onRemove={(tid) => removeTestFromStation(stationIdx, tid)}
                            />
                          ))}
                        </SortableContext>
                        <DragOverlay>
                          {activeId && activeStationIdx === stationIdx ? (
                            <DragOverlayItem testId={activeId} allTests={allTests} />
                          ) : null}
                        </DragOverlay>
                      </DndContext>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

export default StationConfig;
